import Graph from 'graphology';
import { useCallback, useEffect } from 'react';
import Sigma from 'sigma';
import { SigmaNodeEventPayload } from 'sigma/types';

import { ImageCache } from '@/components/nodeGraph/hooks/useImageCache';
import { getEntityImage } from '@/components/nodeGraph/utils/graphConstruction';
import { ConsolidatedGraph } from '@/components/nodeGraph/utils/types';
import { GlobalState } from '@/state/global.state';
import { Asset, Risk, Seed, Technology } from '@/types';

const highlightEdge = ({
  graph,
  source,
  target,
}: {
  graph: Graph;
  source: string;
  target: string;
}) => {
  const edgeId = graph.edge(source, target);
  if (edgeId) {
    graph.setEdgeAttribute(edgeId, 'color', 'rgb(122, 90, 241)');
    graph.setEdgeAttribute(edgeId, 'size', 0.5);
  }
};

const highlightNode = ({
  graph,
  nodeId,
  isHighlighted,
  imageCache,
}: {
  graph: Graph;
  nodeId: string;
  isHighlighted: boolean;
  imageCache: ImageCache;
}) => {
  const image = getEntityImage({
    entityType: graph.getNodeAttribute(nodeId, 'entityType'),
    imageCache,
    node: {
      key: nodeId,
      data: graph.getNodeAttribute(nodeId, 'entityData'),
    } as ConsolidatedGraph[string],
    isHighlighted,
  });

  if (image) {
    graph.setNodeAttribute(nodeId, 'image', image);
  }
};

const highlightAllEdges = ({
  graph,
  selectedNodes,
  currentTheme,
  edgeSize,
}: {
  graph: Graph;
  selectedNodes: Set<string>;
  currentTheme: 'light' | 'dark';
  edgeSize: number;
}) => {
  graph.forEachEdge(edgeId => {
    if (
      !selectedNodes.has(graph.source(edgeId)) ||
      !selectedNodes.has(graph.target(edgeId))
    ) {
      graph.setEdgeAttribute(
        edgeId,
        'color',
        currentTheme === 'light' ? 'rgb(217, 217, 233)' : 'rgb(77, 80, 124)'
      );
      graph.setEdgeAttribute(edgeId, 'size', edgeSize);
    }
  });
};

/**
 * useGraphEventHandlers - Graph Event Handler Management Hook
 *
 * This hook manages all graph interaction events including hover, click,
 * and double-click handlers with proper cleanup and state management.
 *
 * Features:
 * - Node hover effects with highlighting
 * - Click handling for asset/risk selection
 * - Double-click handling for drawer opening
 * - Theme-aware styling
 * - Proper event cleanup
 *
 * @param graphRef - Reference to the graph instance
 * @param currentTheme - Current theme (light/dark)
 * @param selectedNodes - Currently selected nodes
 * @param assets - Array of assets
 * @param drawerState - Drawer state management
 */
export const useGraphEventHandlers = ({
  currentTheme,
  selectedNodes,
  imageCache,
  assets,
  seeds,
  risks,
  technologies,
  drawerState,
  sigma,
}: {
  currentTheme: 'light' | 'dark';
  selectedNodes: Set<string>;
  imageCache: ImageCache;
  assets: Asset[];
  seeds: Seed[];
  risks: Risk[];
  technologies: Technology[];
  drawerState: GlobalState['drawerState'];
  sigma: Sigma;
}) => {
  const handleNodeEnter = useCallback(
    (event: { node: string }) => {
      const hoveredNode = event.node;
      if (!hoveredNode || !sigma) return;

      const graph = sigma.getGraph();

      // REMOVED: Layout stop on hover
      // Layout should stop naturally after initial settling period.
      // Stopping layout on hover was causing issues with layout lifecycle management.

      // Highlight all edges that are connected to the hovered node
      highlightAllEdges({
        graph,
        selectedNodes,
        currentTheme,
        edgeSize: 0.5,
      });

      graph.forEachNeighbor(hoveredNode, neighborId => {
        highlightEdge({
          graph,
          source: hoveredNode,
          target: neighborId,
        });
        highlightEdge({
          graph,
          source: neighborId,
          target: hoveredNode,
        });
        highlightNode({
          graph,
          nodeId: neighborId,
          isHighlighted: true,
          imageCache,
        });
      });

      sigma.refresh();
    },
    [
      sigma,
      selectedNodes,
      currentTheme,
      imageCache, // Now stable - all imageCache methods use useCallback with only currentTheme dependency
    ]
  );

  const handleNodeLeave = useCallback(
    (event: { node: string }) => {
      const hoveredNode = event.node;
      if (!sigma) return;

      const graph = sigma.getGraph();

      // Reset all edges
      highlightAllEdges({
        graph,
        selectedNodes,
        currentTheme,
        edgeSize: 1,
      });

      // Reset all nodes that aren't selected
      graph.forEachNeighbor(hoveredNode, neighborId => {
        highlightNode({
          graph,
          nodeId: neighborId,
          isHighlighted: false,
          imageCache,
        });
      });

      // REMOVED: Layout restart logic
      // The layout should run once during initial construction and then stop naturally.
      // Restarting layout on mouse leave caused continuous animation and prevented
      // the graph from ever reaching a stable resting state.

      sigma.refresh();
    },
    [
      sigma,
      selectedNodes,
      currentTheme,
      imageCache, // Now stable - all imageCache methods use useCallback with only currentTheme dependency
    ]
  );

  const showNodeNeighbors = useCallback(
    (nodeId: string) => {
      if (!sigma) return;

      const graph = sigma.getGraph();

      // Show all neighbors and connecting edges, except riskSeverity and riskStatus
      graph.forEachOutNeighbor(nodeId, neighborId => {
        // Never show riskSeverity and riskStatus nodes
        if (
          neighborId.startsWith('#attribute#riskSeverity') ||
          neighborId.startsWith('#attribute#riskStatus')
        ) {
          return; // Skip these nodes, keep them hidden
        }

        graph.setNodeAttribute(neighborId, 'hidden', false);

        // Find and show the connecting edge
        const edgeId = graph.edge(nodeId, neighborId);
        if (edgeId) {
          graph.setEdgeAttribute(edgeId, 'hidden', false);
        }
      });

      sigma.refresh();
    },
    [sigma]
  );

  const hideNodeNeighbors = useCallback(
    (nodeId: string) => {
      if (!sigma) return;

      const graph = sigma.getGraph();

      // Hide all neighbors and connecting edges from this node
      graph.forEachOutNeighbor(nodeId, neighborId => {
        // Get all edges connected to this neighbor node
        const neighborEdges = graph.edges(neighborId);

        // If neighbor has only one edge (the one to current node), hide it
        if (neighborEdges.length === 1) {
          graph.setNodeAttribute(neighborId, 'hidden', true);
        } else if (
          neighborEdges.length > 1 &&
          // Check if all other edges except the current one are already hidden
          neighborEdges.filter(
            edgeId => graph.getEdgeAttribute(edgeId, 'hidden') === true
          ).length ===
            neighborEdges.length - 1
        ) {
          // If this is the only visible edge left, hide the neighbor node
          graph.setNodeAttribute(neighborId, 'hidden', true);
        }

        // Hide the connecting edge between current node and neighbor
        const edgeId = graph.edge(nodeId, neighborId);
        if (edgeId) {
          graph.setEdgeAttribute(edgeId, 'hidden', true);
        }
      });

      sigma.refresh();
    },
    [sigma]
  );

  const handleClickNode = useCallback(
    (event: SigmaNodeEventPayload) => {
      // Only prevent default Sigma behaviors (zoom/pan), not all default behaviors
      // This allows navigation and other DOM events to work properly
      event.preventSigmaDefault();

      // Do NOT call event.event.original.preventDefault() to allow navigation events
      // Do NOT call event.event.original.stopPropagation() to allow event bubbling

      // Temporarily disable camera zoom
      const camera = sigma.getCamera();
      const originalRatio = camera.getState().ratio;
      camera.setState({ ratio: originalRatio });

      const nodeId = event.node;

      if (nodeId.startsWith('#asset')) {
        const assetToView = assets.find(asset => asset.key === nodeId);
        if (assetToView) {
          drawerState.handleOpenAssetDrawer(assetToView);
        }
      } else if (nodeId.startsWith('#risk#')) {
        const riskToView = risks.find(risk => risk.key === nodeId);
        if (riskToView) {
          drawerState.handleOpenVulnerabilityDrawer(riskToView);
        }
      } else if (nodeId.startsWith('#technology#')) {
        const technologyToView = technologies.find(
          technology => technology.key === nodeId
        );
        if (technologyToView) {
          drawerState.handleOpenTechnologyDrawer(technologyToView);
        }
      } else if (nodeId.startsWith('#seed#')) {
        const seedToView = seeds.find(seed => seed.key === nodeId);
        if (seedToView) {
          drawerState.handleOpenSeedDrawer(seedToView);
        }
      }
    },
    [sigma, assets, risks, technologies, seeds, drawerState]
  );

  useEffect(() => {
    if (!sigma) return;

    // Store references to handlers for proper cleanup
    const enterHandler = handleNodeEnter;
    const leaveHandler = handleNodeLeave;
    const clickHandler = handleClickNode;

    // Add event listeners
    sigma.on('enterNode', enterHandler);
    sigma.on('leaveNode', leaveHandler);
    sigma.on('clickNode', clickHandler);
    sigma.on('doubleClickNode', clickHandler);

    // Cleanup - remove only our specific listeners, not all listeners
    return () => {
      sigma.off('enterNode', enterHandler);
      sigma.off('leaveNode', leaveHandler);
      sigma.off('clickNode', clickHandler);
      sigma.off('doubleClickNode', clickHandler);
    };
  }, [sigma, handleNodeEnter, handleNodeLeave, handleClickNode]);

  return {
    handleNodeEnter,
    handleNodeLeave,
    handleClickNode,
  };
};
